import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { PendingAttachment } from '@/components/inbox/types'

interface UseAudioRecordingParams {
  setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>
  autoSendAudioRef: React.MutableRefObject<boolean>
}

export function useAudioRecording({ setPendingAttachments, autoSendAudioRef }: UseAudioRecordingParams) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingTimerRef = useRef<number | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm'
      const recorder = new MediaRecorder(stream, { mimeType })
      const chunks: BlobPart[] = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
          recordingTimerRef.current = null
        }

        const blob = new Blob(chunks, { type: mimeType })
        const ext = mimeType.startsWith('audio/ogg') ? 'ogg' : 'webm'
        const file = new File([blob], `audio.${ext}`, { type: mimeType })

        setPendingAttachments([{
          id: Math.random().toString(36).substr(2, 9),
          file,
          previewUrl: null,
        }])
        autoSendAudioRef.current = true
        setIsRecording(false)
        setRecordingDuration(0)
      }

      mediaRecorderRef.current = recorder
      recorder.start()
      setIsRecording(true)
      setRecordingDuration(0)

      const startTime = Date.now()
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTime) / 1000))
      }, 1000)
    } catch {
      toast.error('Não foi possível acessar o microfone')
    }
  }, [setPendingAttachments])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
  }, [])

  return {
    isRecording,
    recordingDuration,
    startRecording,
    stopRecording,
  }
}
